import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CircularProgress,
  Pagination,
  InputAdornment,
  ButtonGroup,
} from "@mui/material";

// Icons do MUI
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import StoreIcon from "@mui/icons-material/Store";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SaveIcon from "@mui/icons-material/Save";

import { apiUrl } from "../../../comp/ApiUrl";
import Breadcrumb from "../../../comp/Breadcrumb";

const CategoriasDelivery = () => {
  // Estados principais
  const [data, setData] = useState([]);
  const [stores, setStores] = useState([]);
  const [loader, setLoader] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Modais
  const [openNew, setOpenNew] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [trashConfirm, setTrashConfirm] = useState(null);

  // Formulários
  const [dados, setDados] = useState({ nome: "", loja: "", ativo: "Y" });

  useEffect(() => {
    document.title = "Categorias Delivery | FazPedido";
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${apiUrl}/categorias/delivery`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userToken"),
        },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result.categories || []);
        setStores(result.stores || []);
      }
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoader(false);
    }
  };

  // Lógica de Busca
  const filteredData = data.filter((item) =>
    Object.values(item).some(
      (value) =>
        typeof value === "string" &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Lógica de Paginação
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const dataPaginada = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDados((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const moverItem = async (index, direction) => {
    let newData = [...data];
    const actualIndex = (currentPage - 1) * itemsPerPage + index;

    if (direction === "up" && actualIndex > 0) {
      [newData[actualIndex - 1], newData[actualIndex]] = [
        newData[actualIndex],
        newData[actualIndex - 1],
      ];
    } else if (direction === "down" && actualIndex < newData.length - 1) {
      [newData[actualIndex + 1], newData[actualIndex]] = [
        newData[actualIndex],
        newData[actualIndex + 1],
      ];
    }

    newData = newData.map((item, i) => ({ ...item, posicao: i + 1 }));
    setData(newData);

    try {
      await fetch(`${apiUrl}/categorias/delivery/reordenar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userToken"),
        },
        body: JSON.stringify({
          categorias: newData.map((item) => ({
            id: item.id,
            posicao: item.posicao,
          })),
        }),
      });
      toast.success("Ordem atualizada!");
    } catch (e) {
      toast.error("Erro ao salvar ordem");
    }
  };

  const handleSaveNew = async () => {
    try {
      const response = await fetch(`${apiUrl}/categoria/delivery`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userToken"),
        },
        body: JSON.stringify(dados),
      });
      if (response.ok) {
        toast.success("Categoria cadastrada!");
        setOpenNew(false);
        fetchData();
      }
    } catch (e) {
      toast.error("Erro ao salvar");
    }
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${apiUrl}/categoria/delivery`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userToken"),
        },
        body: JSON.stringify(editItem),
      });
      if (response.ok) {
        toast.success("Categoria atualizada!");
        setEditItem(null);
        fetchData();
      }
    } catch (e) {
      toast.error("Erro ao atualizar");
    }
  };

  if (loader) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <TextField
            size="small"
            placeholder="Pesquisar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: "300px" } }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenNew(true)}
          >
            Nova Categoria
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box mb={2} display="flex" gap={1}>
          <Button
            component={Link}
            to="/produtos/categorias"
            variant="outlined"
            startIcon={<StoreIcon />}
          >
            Loja
          </Button>
          <Button
            variant="contained"
            disabled
            startIcon={<LocalShippingIcon />}
          >
            Delivery
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>Posição</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell align="center">N. Produtos</TableCell>
                <TableCell align="center">Ativo</TableCell>
                <TableCell>Criado Em</TableCell>
                <TableCell align="center">Opções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataPaginada.map((item, index) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{item.nome}</TableCell>
                  <TableCell align="center">{item.NProdutos || 0}</TableCell>
                  <TableCell align="center">
                    {item.ativo === "Y" ? "Sim" : "Não"}
                  </TableCell>
                  <TableCell>
                    {item.created_at
                      ? new Date(item.created_at).toLocaleDateString()
                      : "---"}
                  </TableCell>
                  <TableCell align="center">
                    <ButtonGroup size="small">
                      <IconButton
                        color="primary"
                        onClick={() => setEditItem(item)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        disabled={currentPage === 1 && index === 0}
                        onClick={() => moverItem(index, "up")}
                      >
                        <ArrowUpwardIcon />
                      </IconButton>
                      <IconButton
                        disabled={
                          index === dataPaginada.length - 1 &&
                          currentPage === totalPages
                        }
                        onClick={() => moverItem(index, "down")}
                      >
                        <ArrowDownwardIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => setTrashConfirm(item)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ButtonGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, p) => setCurrentPage(p)}
            color="primary"
          />
        </Box>
      </Paper>

      {/* Modal Nova Categoria */}
      <Dialog
        open={openNew}
        onClose={() => setOpenNew(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Adicionar Categoria</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Nome"
            name="nome"
            margin="normal"
            onChange={handleInputChange}
          />
          <Box display="flex" gap={2}>
            <TextField
              select
              fullWidth
              label="Loja"
              name="loja"
              margin="normal"
              value={dados.loja}
              onChange={handleInputChange}
            >
              {stores.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.nome}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              sx={{ width: "150px" }}
              label="Ativo"
              name="ativo"
              margin="normal"
              value={dados.ativo}
              onChange={handleInputChange}
            >
              <MenuItem value="Y">Sim</MenuItem>
              <MenuItem value="N">Não</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveNew}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Editar Categoria */}
      <Dialog
        open={!!editItem}
        onClose={() => setEditItem(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar Categoria</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Nome"
            name="nome"
            margin="normal"
            value={editItem?.nome || ""}
            onChange={handleEditChange}
          />
          <Box display="flex" gap={2}>
            <TextField
              select
              fullWidth
              label="Loja"
              name="loja"
              margin="normal"
              value={editItem?.loja || ""}
              onChange={handleEditChange}
            >
              {stores.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.nome}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              sx={{ width: "150px" }}
              label="Ativo"
              name="ativo"
              margin="normal"
              value={editItem?.ativo || ""}
              onChange={handleEditChange}
            >
              <MenuItem value="Y">Sim</MenuItem>
              <MenuItem value="N">Não</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleUpdate}
          >
            Atualizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Remover */}
      <Dialog open={!!trashConfirm} onClose={() => setTrashConfirm(null)}>
        <DialogTitle color="error">Remover Categoria</DialogTitle>
        <DialogContent>
          Deseja realmente remover a categoria{" "}
          <strong>{trashConfirm?.nome}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrashConfirm(null)}>Cancelar</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CategoriasDelivery;
